import { apiDate, dateKey } from "./slotSelection";
import { editRestrictions, parseOrderDate } from "./orders";
import { orderErrorMessage } from "./checkout";

/**
 * Modifier une commande — règles du récapitulatif en mode édition, portées
 * de l'app mobile (checkout-store.setEditMode, summary.tsx).
 */

const keyOf = (raw) => {
  const d = parseOrderDate(raw);
  if (!d) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** « 2026-09-24 » → { day: "Thursday", date: "2026-09-24" }, la forme de cart.pickup / cart.time. */
export const slotDay = (key) =>
  key ? { date: { day: DAY_NAMES[parseOrderDate(key).getDay()], date: key } } : null;

/**
 * Ce qu'on garde de la commande à l'ouverture de l'édition : les lignes
 * d'origine (pour les `_destroy`), les verrous, le problème à résoudre, le
 * paiement et les créneaux d'origine.
 */
export function editContextFromOrder(order, issue = null) {
  return {
    orderId: order.id,
    woshId: order.wosh_order_id || null,
    originalItems: (order.product_orders ?? order.products ?? []).map((line) => ({
      orderItemId: line.id,
      productId: line.product_id,
    })),
    restrictions: editRestrictions(order),
    issue: issue === "pickup" || issue === "delivery" ? issue : null,
    paymentStatus: order.payment_status || null,
    paymentType: order.payment_type || null,
    paymentMethodId: order.payment_method_id || null,
    pickup: { key: keyOf(order.pickup_date), slot: order.pickup_slot || null },
    delivery: { key: keyOf(order.delivery_date), slot: order.delivery_slot || null },
  };
}

/**
 * Le problème reste ouvert tant que le créneau manqué est toujours celui
 * de la commande : l'alerte disparaît dès qu'un autre est choisi.
 */
export function issueOpen(context, { pickupKey, pickupSlot, deliveryKey, deliverySlot }) {
  if (context?.issue === "pickup") {
    return pickupKey === context.pickup.key && pickupSlot === context.pickup.slot;
  }
  if (context?.issue === "delivery") {
    return deliveryKey === context.delivery.key && deliverySlot === context.delivery.slot;
  }
  return false;
}

/**
 * Lignes du PUT : quantité des lignes gardées (avec leur id), nouvelles
 * lignes sans id, et `_destroy` pour celles retirées du panier. Une ligne
 * du panier porte l'id produit dans `id` (voir cartReducer).
 */
export function updateAttributes(cartItems, originalItems) {
  const byProduct = new Map((originalItems || []).map((item) => [String(item.productId), item.orderItemId]));
  const current = (cartItems || []).map((line) => {
    const productId = line.product_id ?? line.id;
    const orderItemId = byProduct.get(String(productId));
    return orderItemId != null
      ? { id: orderItemId, product_id: productId, quantity: line.quantity }
      : { product_id: productId, quantity: line.quantity };
  });
  const kept = new Set(current.map((line) => String(line.product_id)));
  const removed = (originalItems || [])
    .filter((item) => !kept.has(String(item.productId)))
    .map((item) => ({ id: item.orderItemId, _destroy: true }));
  return [...current, ...removed];
}

/**
 * Corps de PUT /customer/orders/:id. Le paiement est omis sur une commande
 * déjà payée (jamais `payment_type: ""`, que Rails refuse) ;
 * `pick_up_issue_fix` / `delivery_update` seulement pour résoudre un
 * créneau manqué.
 */
export function buildUpdatePayload({
  context,
  cartItems,
  pickup,
  pickupSlot,
  delivery,
  deliverySlot,
  address,
  isHome,
  description,
  payment,
}) {
  const order = {
    orderable_type: isHome ? "DeliveryAddress" : "CollectionPoint",
    orderable_id: address?.id || null,
    pickup_date: apiDate({ day: pickup?.date?.day, key: dateKey(pickup?.date?.date) }),
    pickup_slot: pickupSlot,
    delivery_date: apiDate({ day: delivery?.date?.day, key: dateKey(delivery?.date?.date) }),
    delivery_slot: deliverySlot,
    product_orders_attributes: updateAttributes(cartItems, context.originalItems),
  };

  // La liste des commandes ne renvoie pas les instructions : on ne les
  // envoie que si l'utilisateur les a écrites, pour ne pas les effacer.
  if (description !== undefined) order.description = description;
  if (context.paymentStatus !== "paid" && payment) {
    order.payment_type = payment.type;
    if (payment.type === "Card") order.payment_method_id = payment.cardId;
  }
  if (context.issue === "pickup") order.pick_up_issue_fix = true;
  if (context.issue === "delivery") order.delivery_update = true;

  return { order };
}

/** Message d'un refus du serveur, traduit quand on le reconnaît. */
export function editErrorMessage(data, t) {
  const raw = typeof data?.message === "string" ? data.message : "";
  if (/cannot be edited/i.test(raw)) return t("checkout.apiOrderNotEditable");
  if (/slot not available/i.test(raw)) return t("checkout.apiSlotUnavailable");
  if (/laundry is closed/i.test(raw)) return t("checkout.apiLaundryClosed");
  return orderErrorMessage(data) || t("checkout.apiGenericFailure");
}

/** TVA belge (21 %) comprise dans un montant TTC. */
export const vatIncluded = (total) => Math.round((Number(total) || 0) * (21 / 121) * 100) / 100;
